import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Business as BusinessIcon,
  Handshake as HandshakeIcon,
  EmojiEvents as EmojiEventsIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import NumberFormatText from '../../../common/form/NumberFormatText';
import EmpModalContainer from '../../../../containers/common/form/modal/EmpModalContainer';
import { formatNumber, ProfitVisualization } from './CommonComponents';

/**
 * 개선된 매출이익 입력 폼 (V2)
 * 
 * 개선사항:
 * - 시각적 이익 구조 차트
 * - 내부거래비용 다중 입력
 * - 실시간 계산 및 비율 표시
 * - 직관적인 카드 레이아웃
 */
const SalesProfitFormV2 = ({ formik, project }) => {
  const [openEmpModal, setOpenEmpModal] = useState(false);
  const [selectedInternalIndex, setSelectedInternalIndex] = useState(null);

  // 총 매출액
  const totalSales = project?.totalSales || 0;

  // 퍼센트 계산
  const getPercent = (value) => {
    if (!totalSales || totalSales === 0) return 0;
    return ((Number(value || 0) / totalSales) * 100).toFixed(1);
  };

  // 내부거래비용 합계
  const totalInternalAmt = useMemo(() => {
    const costs = formik.values.roInternalCosts || [];
    return costs.reduce((sum, item) => sum + Number(item.roInternalAmt || 0), 0);
  }, [formik.values.roInternalCosts]);

  // 최종 매출이익 계산
  const calculatedFinalProfit = useMemo(() => {
    const profit = Number(formik.values.roProfitAmt || 0);
    const cost = Number(formik.values.roCostAmt || 0);
    return profit - cost - totalInternalAmt;
  }, [formik.values.roProfitAmt, formik.values.roCostAmt, totalInternalAmt]);

  // 최종 이익 업데이트
  useEffect(() => {
    formik.setFieldValue('roAdjProfitAmt', calculatedFinalProfit);
    formik.setFieldValue('roAdjProfitAmtPercent', getPercent(calculatedFinalProfit));
  }, [calculatedFinalProfit]);

  // 매출이익 변경 시 비율 업데이트
  const handleProfitChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('roProfitAmt', value);
    formik.setFieldValue('roProfitAmtPercent', getPercent(value));
  };

  // 영업비용 변경 시 비율 업데이트
  const handleCostChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('roCostAmt', value);
    formik.setFieldValue('roCostAmtPercent', getPercent(value));
  };

  // 내부거래비용 추가
  const handleAddInternalCost = () => {
    const currentCosts = formik.values.roInternalCosts || [];
    formik.setFieldValue('roInternalCosts', [
      ...currentCosts,
      {
        roPartnerUsrNm: '',
        roPartnerUsr: '',
        roInternalAmt: 0,
        roInternalAmtPercent: 0,
      },
    ]);
  };

  // 내부거래비용 삭제
  const handleRemoveInternalCost = (index) => {
    const currentCosts = [...(formik.values.roInternalCosts || [])];
    currentCosts.splice(index, 1);
    formik.setFieldValue('roInternalCosts', currentCosts);
  };

  // 내부거래비용 금액 변경
  const handleInternalAmtChange = (index, value) => {
    const currentCosts = [...(formik.values.roInternalCosts || [])];
    currentCosts[index] = {
      ...currentCosts[index],
      roInternalAmt: value,
      roInternalAmtPercent: getPercent(value),
    };
    formik.setFieldValue('roInternalCosts', currentCosts);
  };

  // 사원 선택 모달 열기
  const handleOpenEmpModal = (index) => {
    setSelectedInternalIndex(index);
    setOpenEmpModal(true);
  };

  // 사원 선택 완료
  const handleSelectEmployee = (emp) => {
    if (selectedInternalIndex !== null) {
      const currentCosts = [...(formik.values.roInternalCosts || [])];
      currentCosts[selectedInternalIndex] = {
        ...currentCosts[selectedInternalIndex],
        roPartnerUsrNm: emp.usrNm,
        roPartnerUsr: emp.usrUuid,
      };
      formik.setFieldValue('roInternalCosts', currentCosts);
    }
    setOpenEmpModal(false);
    setSelectedInternalIndex(null);
  };

  const profitStatus = calculatedFinalProfit >= 0 ? 'positive' : 'negative';

  return (
    <Box>
      {/* 매출이익 시각화 */}
      <ProfitVisualization
        totalSales={totalSales}
        profit={Number(formik.values.roProfitAmt || 0)}
        cost={Number(formik.values.roCostAmt || 0)}
        internalCost={totalInternalAmt}
      />

      <Stack spacing={2.5}>
        {/* 매출이익 입력 */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2.5,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: 'success.main',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ 
              width: 44, height: 44, borderRadius: '50%', 
              bgcolor: 'success.50',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TrendingUpIcon color="success" />
            </Box>
            <Typography variant="subtitle1" fontWeight={600}>
              매출이익
            </Typography>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                id="roProfitAmt"
                name="roProfitAmt"
                label="매출이익 금액"
                value={formik.values.roProfitAmt || 0}
                onChange={handleProfitChange}
                error={formik.touched.roProfitAmt && Boolean(formik.errors.roProfitAmt)}
                helperText={formik.touched.roProfitAmt && formik.errors.roProfitAmt}
                InputProps={{
                  inputComponent: NumberFormatText,
                  endAdornment: (
                    <InputAdornment position="end">원</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`매출액 대비 ${getPercent(formik.values.roProfitAmt)}%`}
                color="success"
                variant="outlined"
                sx={{ width: '100%', height: 40, fontSize: '0.95rem' }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* 내부거래비용 입력 */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2.5,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: 'info.main',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                width: 44, height: 44, borderRadius: '50%', 
                bgcolor: 'info.50',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <HandshakeIcon color="info" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  내부거래비용
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  협업본부와의 내부 거래 비용
                </Typography>
              </Box>
            </Stack>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddInternalCost}
              variant="outlined"
            >
              추가
            </Button>
          </Stack>

          {formik.values.roInternalCosts && formik.values.roInternalCosts.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, width: '40%' }}>협업본부 영업담당</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>거래비용</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '15%' }} align="center">비율</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '10%' }} align="center">삭제</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formik.values.roInternalCosts.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            value={item.roPartnerUsrNm || ''}
                            placeholder="담당자 선택"
                            disabled
                            sx={{ flex: 1 }}
                            InputProps={{
                              startAdornment: item.roPartnerUsrNm ? (
                                <InputAdornment position="start">
                                  <Avatar sx={{ width: 24, height: 24, fontSize: 11 }}>
                                    {item.roPartnerUsrNm[0]}
                                  </Avatar>
                                </InputAdornment>
                              ) : null,
                            }}
                            error={
                              formik.touched.roInternalCosts?.[index]?.roPartnerUsrNm &&
                              Boolean(formik.errors.roInternalCosts?.[index]?.roPartnerUsrNm)
                            }
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenEmpModal(index)}
                            startIcon={<PersonIcon />}
                          >
                            선택
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={item.roInternalAmt || 0}
                          onChange={(e) => handleInternalAmtChange(index, e.target.value)}
                          InputProps={{
                            inputComponent: NumberFormatText,
                            endAdornment: <InputAdornment position="end">원</InputAdornment>,
                          }}
                          error={
                            formik.touched.roInternalCosts?.[index]?.roInternalAmt &&
                            Boolean(formik.errors.roInternalCosts?.[index]?.roInternalAmt)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${getPercent(item.roInternalAmt)}%`}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveInternalCost(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* 합계 행 */}
                  <TableRow sx={{ bgcolor: 'info.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>합계</TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {formatNumber(totalInternalAmt)}원
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${getPercent(totalInternalAmt)}%`}
                        size="small"
                        color="info"
                      />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center', 
              bgcolor: 'grey.50', 
              borderRadius: 1 
            }}>
              <Typography color="text.secondary">
                등록된 내부 거래비용이 없습니다.
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddInternalCost}
                sx={{ mt: 1 }}
              >
                내부거래비용 추가
              </Button>
            </Box>
          )}
        </Paper>

        {/* 영업비용 입력 */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2.5,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: 'warning.main',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ 
              width: 44, height: 44, borderRadius: '50%', 
              bgcolor: 'warning.50',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BusinessIcon color="warning" />
            </Box>
            <Typography variant="subtitle1" fontWeight={600}>
              영업비용
            </Typography>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                id="roCostAmt"
                name="roCostAmt"
                label="예상 영업비용"
                value={formik.values.roCostAmt || 0}
                onChange={handleCostChange}
                error={formik.touched.roCostAmt && Boolean(formik.errors.roCostAmt)}
                helperText={formik.touched.roCostAmt && formik.errors.roCostAmt}
                InputProps={{
                  inputComponent: NumberFormatText,
                  endAdornment: (
                    <InputAdornment position="end">원</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`매출액 대비 ${getPercent(formik.values.roCostAmt)}%`}
                color="warning"
                variant="outlined"
                sx={{ width: '100%', height: 40, fontSize: '0.95rem' }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* 최종 매출이익 (계산 결과) */}
        <Paper 
          elevation={2}
          sx={{ 
            p: 3,
            borderRadius: 2,
            background: profitStatus === 'positive' 
              ? 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)'
              : 'linear-gradient(135deg, #ffebee 0%, #ffffff 100%)',
            border: '2px solid',
            borderColor: profitStatus === 'positive' ? 'success.main' : 'error.main',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                width: 52, height: 52, borderRadius: '50%', 
                bgcolor: profitStatus === 'positive' ? 'success.main' : 'error.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <EmojiEventsIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  최종 매출이익
                </Typography>
                <Typography 
                  variant="h4" 
                  fontWeight={700}
                  color={profitStatus === 'positive' ? 'success.main' : 'error.main'}
                >
                  {formatNumber(calculatedFinalProfit)}원
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Chip 
                icon={profitStatus === 'positive' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${getPercent(calculatedFinalProfit)}%`}
                color={profitStatus === 'positive' ? 'success' : 'error'}
                sx={{ fontWeight: 700, fontSize: '1.1rem', height: 40, px: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                매출액 대비 이익률
              </Typography>
            </Box>
          </Stack>

          {/* 계산식 */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            매출이익 ({formatNumber(formik.values.roProfitAmt)}) 
            - 내부거래 ({formatNumber(totalInternalAmt)}) 
            - 영업비용 ({formatNumber(formik.values.roCostAmt)}) 
            = 최종이익 ({formatNumber(calculatedFinalProfit)})
          </Typography>
        </Paper>
      </Stack>

      {/* 사원 선택 모달 */}
      {openEmpModal && (
        <EmpModalContainer
          title="협업 영업담당자 선택"
          multi={false}
          open={openEmpModal}
          handleClose={() => {
            setOpenEmpModal(false);
            setSelectedInternalIndex(null);
          }}
          selectedEmployee={handleSelectEmployee}
        />
      )}
    </Box>
  );
};

export default SalesProfitFormV2;
